import {
  CARRY_ONE,
  CARRY_TWO,
  CLIMB,
  CONCEAL,
  HANG,
  IDLE,
  KNOCK,
  MANTLE,
  PUSH,
  RIDE,
  RUN,
  SIT,
  THROW,
  WALK,
} from './gait.js';

/**
 * Which clip plays for which state.
 *
 * Pure: a state from `gait.js` and the plain reading it came from, in; the
 * names of the clips to play and how fast, out. The clips are the ones in
 * `public/models/figure.glb` (built by `assets-src/build_character.py` from the
 * Mixamo downloads), named by the game rather than by Mixamo.
 *
 * A body plays two things at once, because a person is two things at once:
 *
 *   base    the whole body: the legs and, unless something else claims them,
 *           the arms too
 *   upper   what the arms and the torso are doing instead, laid over the legs
 *           of the base: throwing. Only over a base that leaves the arms free
 *           (standing, walking, running, riding the deck of a cart); a body
 *           climbing a wall has no arms to spare
 *   hold    carrying, which is the base's own arms held in place (see HOLD)
 *
 * States with no clip of their own take the nearest one: a mantle is the end
 * of a climb, the golf cart seat is a seat, hiding is crouching, standing on a
 * ridden cart is standing. Being knocked over plays the door clip, by D's
 * brief. Somebody in a conversation who is otherwise standing plays talking.
 */
export const BASE_CLIP = Object.freeze({
  [IDLE]: 'idle',
  [WALK]: 'walk',
  [RUN]: 'run',
  [PUSH]: 'push',
  [RIDE]: 'idle',
  [CLIMB]: 'climb',
  [MANTLE]: 'climb',
  [HANG]: 'hang',
  [CONCEAL]: 'crouch',
  [KNOCK]: 'knock',
  [SIT]: 'sit',
});

export const UPPER_CLIP = Object.freeze({
  [THROW]: 'throw',
});

/**
 * Carrying is not a clip. None of the downloaded ones holds a thing the way a
 * banquet server does (the box walk carries it on one raised forearm with the
 * other arm out), so the body keeps playing its own idle or walk and the arms
 * are held over it (`figure.js`): one-handed, the item in the right hand and
 * that forearm bent to it; two-handed, both arms in front, elbows in,
 * forearms level. Which one is the mass table's, through the state.
 */
export const HOLD = Object.freeze({
  [CARRY_ONE]: 'one',
  [CARRY_TWO]: 'two',
});

/** Bases whose arms are free for an upper-body clip. */
const ARMS_FREE = new Set([IDLE, WALK, RUN, RIDE]);

/**
 * Bases whose arms can hold something: on your feet. Riding a cart's deck, the
 * load is on the cart and the hands are on the handle.
 */
const HANDS_FREE = new Set([IDLE, WALK, RUN]);

/** Bases whose clip covers ground, played at the speed the body really goes. */
export const STRIDING = new Set(['walk', 'run', 'push']);

/**
 * How far into the throw clip a full wind-up reaches: the arm drawn back, not
 * yet let go. The rest of the clip is the follow-through, played on release.
 */
export const WINDUP_REACH = 0.42;

/**
 * Where the run starts coming in whatever the cadence says, and where it is
 * the whole of it (m/s). Ours: a sprint should look like one, so the run is all
 * there is from 4.2 (where the run clip plays at 0.73 and its planted foot is
 * flat; with a quarter of the walk still in it the foot rocked heel to toe and
 * was barely ever down); under 2.8 a body is walking, however briskly. Between
 * them is a jog, which only sprinting with an armful ever settles in.
 */
export const RUN_FROM = 2.8;

/** The run's cadence share is eased in over this fraction of the walk's own speed above it. */
const EASE_OVER = 0.6;
export const RUN_FULL = 4.2;

/**
 * The walk-to-run blend at a speed: one locomotion, not two states.
 *
 * Every open-world game since Paragon plays walking out of a blend keyed on
 * ground speed (`docs/feel-reference.md`, section 1). Two things decide the
 * run's share here:
 *
 *   the cadence   above the walk clip's own speed a person lengthens their
 *                 stride as well as quickening it. Cadence rising with the
 *                 square root of speed is the usual human curve, and the stride
 *                 that leaves is made by mixing in the run's longer one. So the
 *                 walk is never played far faster than it was captured: at the
 *                 player's 2.7 m/s it was 1.65 times, a strut at 185 steps a
 *                 minute, and is now about a fifth run at 1.2 strides a second
 *   the sprint    `RUN_FROM` to `RUN_FULL`, so a sprint is a run
 *
 * `gaits` is what the file says about each clip: `speed` (m/s at 1x, measured
 * off the planted foot) and `stride` (metres one whole cycle covers). Back
 * comes each clip's weight and `cycle`, whole cycles a second, which is the
 * blended stride covered at the body's speed: what keeps a planted foot
 * planted while two clips of different lengths play from one phase.
 */
export function locomotion(speed, gaits = {}) {
  const walk = gaits.walk;
  const run = gaits.run;
  const v = Math.max(0, speed);
  if (!walk?.stride || !walk?.speed) return null;
  if (!run?.stride || !run?.speed) return { walk: 1, run: 0, cycle: v / walk.stride };
  const span = run.stride - walk.stride;
  let byCadence = 0;
  if (v > walk.speed && span > 0) {
    const stride = walk.stride * Math.sqrt(v / walk.speed);
    // Eased in over the first stretch above the walk's own speed: a little run
    // in a walk puts the run's swinging foot into the walk's planted one, and
    // a walk a third quicker than captured is still a walk.
    const ease = Math.min(1, Math.max(0, (v - walk.speed) / (walk.speed * EASE_OVER)));
    byCadence = Math.min(1, Math.max(0, (stride - walk.stride) / span)) * ease * ease;
  }
  const t = Math.min(1, Math.max(0, (v - RUN_FROM) / (RUN_FULL - RUN_FROM)));
  const bySprint = t * t * (3 - 2 * t);
  const r = Math.max(byCadence, bySprint);
  const stride = (1 - r) * walk.stride + r * run.stride;
  return { walk: 1 - r, run: r, cycle: stride > 0 ? v / stride : 0 };
}

/**
 * The clips a state plays.
 *
 * `speeds` is how fast each striding clip covers ground at 1x (m/s, measured
 * off the planted foot when the file was built). A striding clip plays at the
 * body's speed over its own, so a planted foot stays planted; standing still
 * on a striding base holds the clip where it is.
 *
 * `gaits`, when the file has been studied (see `locomotion`), turns walking and
 * running into one blend: `blend` is each clip's weight and `cycle` how fast
 * the shared phase turns, and `base` is whichever clip has the most of it.
 */
export function clipsFor(state, body = {}, speeds = {}, gaits = null) {
  let base = BASE_CLIP[state.base] ?? 'idle';
  if (base === 'idle' && body.talking && state.base === IDLE) base = 'talk';
  const upper = ARMS_FREE.has(state.base) ? UPPER_CLIP[state.overlay] ?? null : null;
  // Sat at a table with something in one hand (somebody on a break with a
  // phone or a sandwich) holds it too; sat anywhere else, the hands are busy.
  const free = HANDS_FREE.has(state.base) || (state.base === SIT && body.atTable && state.overlay === CARRY_ONE);
  const hold = free ? HOLD[state.overlay] ?? null : null;
  let timeScale = 1;
  let blend = null;
  const speed = Math.max(0, body.speed ?? 0);
  if ((state.base === WALK || state.base === RUN) && gaits) {
    const mix = locomotion(speed, gaits);
    if (mix) {
      blend = mix;
      base = mix.run > mix.walk ? 'run' : 'walk';
    }
  }
  if (!blend && STRIDING.has(base)) {
    const natural = speeds[base] ?? 0;
    timeScale = natural > 0 ? speed / natural : 1;
  }
  const upperScale = upper === 'throw' ? 0 : 1;
  return { base, upper, hold, timeScale, upperScale, blend, windup: upper === 'throw' ? body.windup ?? 0 : 0 };
}
